import React, { useState, useMemo } from 'react';
import './PricingCalculator.css';
import ProjectionsTable from './ProjectionsTable';
import PDFExport from './PDFExport';
import ShareQuote from './ShareQuote';
import {
    priceQuote,
    makeMoneyFormatter,
    SUBSCRIBER_RATIOS
} from '../lib/pricing';
import { useSettings } from '../lib/SettingsContext';
import APP_CONFIG, {
    TIER_ORDER,
    getBillablePlatforms,
    getCoreFeatures,
    getOptionalFeatures,
    getDefaultVideoQuality,
    getCurrencySymbol,
    getVideoQualityOptions,
    formatCapacity
} from '../config/appConfig';

/** Small reusable "?" help bubble. */
const Tooltip = ({ text }) => (
    <div className="tooltip-container">
        <span className="question-mark">?</span>
        <div className="tooltip">{text}</div>
    </div>
);

const PricingCalculator = () => {
    // Team-wide calibration from the database; falls back to compiled defaults.
    const { effective: calculationSettings, values: settingsValues } = useSettings();

    const [billingType, setBillingType] = useState(APP_CONFIG.defaults.billingType);
    const [showProjections, setShowProjections] = useState(APP_CONFIG.defaults.showProjections);
    const [currency, setCurrency] = useState(APP_CONFIG.defaults.currency);

    // Usage inputs
    const [videoAssets, setVideoAssets] = useState(APP_CONFIG.defaults.videoAssets);
    const [avgDuration, setAvgDuration] = useState(APP_CONFIG.defaults.avgDuration);
    const [totalPlays, setTotalPlays] = useState(APP_CONFIG.defaults.totalPlays);
    const [avgWatchTime, setAvgWatchTime] = useState(APP_CONFIG.defaults.avgWatchTime);
    const [videoQuality, setVideoQuality] = useState(getDefaultVideoQuality());

    // Platform and feature selections
    const [selectedPlatforms, setSelectedPlatforms] = useState(APP_CONFIG.defaults.selectedPlatforms);
    const [selectedFeatures, setSelectedFeatures] = useState(APP_CONFIG.defaults.selectedFeatures);

    // Usage-based add-ons: billed on measured usage, never bundled with a tier
    const [ssaiChannels, setSsaiChannels] = useState(APP_CONFIG.defaults.ssaiChannels);
    const [ssaiBandwidthTB, setSsaiBandwidthTB] = useState(APP_CONFIG.defaults.ssaiBandwidthTB);
    const [aiDubbingMinutes, setAiDubbingMinutes] = useState(APP_CONFIG.defaults.aiDubbingMinutes);
    const [aiSubtitleMinutes, setAiSubtitleMinutes] = useState(APP_CONFIG.defaults.aiSubtitleMinutes);

    // Pricing model: 'monthly' (no upfront) or 'legacy' (one-time app fees)
    const [pricingMode, setPricingMode] = useState(APP_CONFIG.defaults.pricingMode);
    const [appDiscount, setAppDiscount] = useState(APP_CONFIG.defaults.appDiscount);

    // Revenue model
    const [revenueModel, setRevenueModel] = useState(APP_CONFIG.defaults.revenueModel);
    const [subscribers, setSubscribers] = useState(APP_CONFIG.defaults.subscribers);
    const [avgSpend, setAvgSpend] = useState(APP_CONFIG.defaults.avgSpend);
    const [impressions, setImpressions] = useState(APP_CONFIG.defaults.impressions);
    const [cpm, setCpm] = useState(APP_CONFIG.defaults.cpm);
    const [transactions, setTransactions] = useState(APP_CONFIG.defaults.transactions);
    const [avgPrice, setAvgPrice] = useState(APP_CONFIG.defaults.avgPrice);

    // Growth rate for projections
    const [monthlyGrowthRate, setMonthlyGrowthRate] = useState(APP_CONFIG.defaults.monthlyGrowthRate);

    const packages = APP_CONFIG.packages;
    const platforms = APP_CONFIG.platforms;
    const features = APP_CONFIG.features;
    const overage = APP_CONFIG.overage;
    const usageCfg = APP_CONFIG.usageAddOns;
    const isLegacy = pricingMode === 'legacy';

    const currencyRates = Object.fromEntries(
        Object.entries(APP_CONFIG.currencies).map(([key, c]) => [key, c.rate])
    );

    // Concurrent-to-total subscriber ratios by content type
    // Everything the pricing engine needs, in the exact shape that gets saved
    // and shared by link. Keeping it in one object means the expert view, the
    // guided wizard and a shared quote are all costed by the same code path.
    const [subscriberEstimationType, setSubscriberEstimationType] = useState('consumer');
    const subscriberRatios = SUBSCRIBER_RATIOS;

    const quoteState = useMemo(() => ({
        videoAssets, avgDuration, totalPlays, avgWatchTime, videoQuality,
        subscriberEstimationType,
        selectedPlatforms, selectedFeatures,
        ssaiChannels, ssaiBandwidthTB, aiDubbingMinutes, aiSubtitleMinutes,
        pricingMode, appDiscount,
        revenueModel, subscribers, avgSpend, impressions, cpm, transactions, avgPrice,
        billingType, currency, monthlyGrowthRate
    }), [
        videoAssets, avgDuration, totalPlays, avgWatchTime, videoQuality,
        subscriberEstimationType, selectedPlatforms, selectedFeatures,
        ssaiChannels, ssaiBandwidthTB, aiDubbingMinutes, aiSubtitleMinutes,
        pricingMode, appDiscount, revenueModel, subscribers, avgSpend,
        impressions, cpm, transactions, avgPrice, billingType, currency, monthlyGrowthRate
    ]);

    const priced = useMemo(() => priceQuote(quoteState, calculationSettings), [quoteState, calculationSettings]);
    const { requirements, revenue, tierCosts, recommendedTier, upgradeInsight } = priced;
    const estimatedSubscribers = priced.activeSubscribers;

    // FORMATTING
    // =========================================================================
    const roundDownPrice = (amount) => {
        const { largeAmountThreshold, smallAmountRounding } = APP_CONFIG.billing.roundingRules;
        if (amount > largeAmountThreshold) return Math.floor(amount / 10) * 10;
        return Math.floor(amount / smallAmountRounding) * smallAmountRounding;
    };

    const formatCurrency = (amount, isMonthly = true) => {
        const converted = amount * currencyRates[currency];
        let finalAmount = converted;
        if (billingType === 'yearly' && isMonthly) {
            finalAmount = converted * 12 * (1 - APP_CONFIG.billing.yearlyDiscount);
        }
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency,
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(roundDownPrice(finalAmount));
    };

    const getPeriodText = () => (billingType === 'yearly' ? 'year' : 'month');

    // formatCurrency floors to whole units, which turns a $0.01/GB rate into
    // "$0". Per-GB rates need their own formatter.
    const formatRate = makeMoneyFormatter(currency);

    const calculateYearlySavings = (monthlyAmount) => {
        const converted = monthlyAmount * currencyRates[currency];
        const savings = converted * 12 * APP_CONFIG.billing.yearlyDiscount;
        return formatCurrency(roundDownPrice(savings), false);
    };

    const togglePlatform = (key) => {
        setSelectedPlatforms(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const toggleFeature = (key) => {
        setSelectedFeatures(prev => ({ ...prev, [key]: !prev[key] }));
    };

    const selectedPlatformCount = Object.entries(selectedPlatforms)
        .filter(([k, on]) => on && platforms[k]).length;
    const selectedFeatureCount = Object.entries(selectedFeatures)
        .filter(([k, on]) => on && features[k] && !features[k].core).length;

    /** One row of the comparison grid, rendered once per tier. */
    const CostRow = ({ label, tooltip, render, className = '' }) => (
        <div className={`cost-row ${className}`}>
            <div className="cost-label">
                {label}
                {tooltip && <Tooltip text={tooltip} />}
            </div>
            {TIER_ORDER.map(tierKey => (
                <React.Fragment key={tierKey}>{render(tierKey, tierCosts[tierKey])}</React.Fragment>
            ))}
        </div>
    );

    return (
        <div className="pricing-calculator">
            <div className="container">
                <div className="calculator-header">
                    <div className="header-controls">
                        <label className="checkbox-label">
                            <input
                                type="checkbox"
                                checked={showProjections}
                                onChange={(e) => setShowProjections(e.target.checked)}
                            />
                            Show Projections
                        </label>

                        <select
                            value={currency}
                            onChange={(e) => setCurrency(e.target.value)}
                            className="currency-select"
                        >
                            {Object.entries(APP_CONFIG.currencies).map(([code, info]) => (
                                <option key={code} value={code}>{info.symbol} - {code}</option>
                            ))}
                        </select>

                        <div className="billing-toggle">
                            <button
                                className={`billing-option ${billingType === 'monthly' ? 'active' : ''}`}
                                onClick={() => setBillingType('monthly')}
                            >
                                Monthly
                            </button>
                            <button
                                className={`billing-option ${billingType === 'yearly' ? 'active' : ''}`}
                                onClick={() => setBillingType('yearly')}
                            >
                                Yearly - Save {Math.round(APP_CONFIG.billing.yearlyDiscount * 100)}%
                            </button>
                        </div>

                        <div className="pricing-mode-toggle">
                            <span className="pricing-mode-label">
                                Apps
                                <Tooltip text={APP_CONFIG.tooltips.pricingMode} />
                            </span>
                            <button
                                className={`billing-option ${!isLegacy ? 'active' : ''}`}
                                onClick={() => setPricingMode('monthly')}
                            >
                                Monthly · no upfront
                            </button>
                            <button
                                className={`billing-option ${isLegacy ? 'active' : ''}`}
                                onClick={() => setPricingMode('legacy')}
                            >
                                Legacy one-time
                            </button>
                        </div>
                    </div>
                </div>

                <div className="calculator-content">
                    <div className="left-panel">
                        <div className="panel">
                            <h2>Your Monthly Requirements</h2>
                            <p className="panel-subtitle">Configure your video streaming requirements and business model</p>

                            <div className="section">
                                <h3>Usage</h3>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>
                                            Total Number of Video Assets
                                            <Tooltip text={APP_CONFIG.tooltips.videoAssets} />
                                        </label>
                                        <div className="input-with-icon">
                                            <span className="input-icon">🎬</span>
                                            <input
                                                type="number"
                                                value={videoAssets}
                                                onChange={(e) => setVideoAssets(Number(e.target.value))}
                                            />
                                        </div>
                                    </div>

                                    <div className="input-group">
                                        <label>
                                            Average Video Duration
                                            <Tooltip text={APP_CONFIG.tooltips.avgDuration} />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">⏱️</span>
                                            <input
                                                type="number"
                                                value={avgDuration}
                                                onChange={(e) => setAvgDuration(Number(e.target.value))}
                                            />
                                            <span>min</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>
                                            Total concurrent users watching
                                            <Tooltip text={APP_CONFIG.tooltips.totalPlays} />
                                        </label>
                                        <div className="input-with-icon">
                                            <span className="input-icon">👥</span>
                                            <input
                                                type="number"
                                                value={totalPlays}
                                                onChange={(e) => setTotalPlays(Number(e.target.value))}
                                            />
                                        </div>
                                        <div className="subscriber-estimation">
                                            <div className="estimation-header">
                                                <span className="estimation-label">Approx. active subscribers:</span>
                                                <span className="estimation-value">{estimatedSubscribers.toLocaleString()}</span>
                                            </div>
                                            <div className="estimation-details">
                                                <select
                                                    value={subscriberEstimationType}
                                                    onChange={(e) => setSubscriberEstimationType(e.target.value)}
                                                    className="estimation-type-select"
                                                >
                                                    {Object.entries(subscriberRatios).map(([key, config]) => (
                                                        <option key={key} value={key}>
                                                            {config.name} (1:{config.ratio} ratio)
                                                        </option>
                                                    ))}
                                                </select>
                                                <div className="ratio-info">
                                                    {subscriberRatios[subscriberEstimationType].description}
                                                </div>
                                            </div>
                                            <div className="estimation-explanation">
                                                {APP_CONFIG.tooltips.activeSubscribers}
                                            </div>
                                        </div>
                                    </div>

                                    <div className="input-group">
                                        <label>
                                            Average Watch Time
                                            <Tooltip text={APP_CONFIG.tooltips.avgWatchTime} />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">⏰</span>
                                            <input
                                                type="number"
                                                value={avgWatchTime}
                                                onChange={(e) => setAvgWatchTime(Number(e.target.value))}
                                            />
                                            <span>min</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="input-group">
                                    <label>
                                        Video Quality
                                        <Tooltip text={APP_CONFIG.tooltips.videoQuality} />
                                    </label>
                                    <div className="select-with-icon">
                                        <span className="input-icon">📺</span>
                                        <select value={videoQuality} onChange={(e) => setVideoQuality(e.target.value)}>
                                            {getVideoQualityOptions().map(option => (
                                                <option key={option.value} value={option.value}>{option.label}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            {/* ---------------- PLATFORMS ---------------- */}
                            <div className="section">
                                <div className="section-header">
                                    <h3>
                                        Platforms &amp; Apps
                                        {selectedPlatformCount > 0 && (
                                            <span className="selection-count">{selectedPlatformCount} selected</span>
                                        )}
                                    </h3>
                                    <Tooltip text={APP_CONFIG.tooltips.platforms} />
                                </div>
                                <p className="section-note">
                                    {isLegacy
                                        ? 'Legacy model: each app is a one-time development fee.'
                                        : `Included by tier. Any platform your tier doesn't cover is a flat ${formatCurrency(150, false)}/month.`}
                                </p>

                                <div className="selection-grid">
                                    <label className="selection-item included-always">
                                        <input
                                            type="checkbox"
                                            checked
                                            readOnly
                                            aria-readonly="true"
                                            tabIndex={-1}
                                            onClick={(e) => e.preventDefault()}
                                        />
                                        <span className="selection-body">
                                            <span className="selection-name">
                                                <span className="selection-icon">{platforms.web.icon}</span>
                                                {platforms.web.name}
                                            </span>
                                            <span className="selection-meta">
                                                <span className="badge badge-included">Included in the package</span>
                                            </span>
                                        </span>
                                    </label>

                                    {getBillablePlatforms().map(([key, platform]) => (
                                        <label key={key} className={`selection-item ${selectedPlatforms[key] ? 'selected' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={!!selectedPlatforms[key]}
                                                onChange={() => togglePlatform(key)}
                                            />
                                            <span className="selection-body">
                                                <span className="selection-name">
                                                    <span className="selection-icon">{platform.icon}</span>
                                                    {platform.name}
                                                </span>
                                                <span className="selection-meta">
                                                    <span className="selection-price">
                                                        {isLegacy
                                                            ? (APP_CONFIG.legacyAppBundles[key]
                                                                ? `${formatCurrency(APP_CONFIG.legacyAppBundles[key].price, false)} one-time`
                                                                : 'Not in legacy catalogue')
                                                            : `${formatCurrency(platform.price, true)}/${getPeriodText()}`}
                                                    </span>
                                                    {!isLegacy && (
                                                        platform.includedFrom
                                                            ? <span className="badge badge-from">Free from {packages[platform.includedFrom].name}</span>
                                                            : <span className="badge badge-addon">Add-on only</span>
                                                    )}
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>

                                {isLegacy && (
                                    <div className="discount-section">
                                        <label>
                                            One-Time Apps Discount (%)
                                            <Tooltip text="Discount applied to legacy one-time app development costs." />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">💰</span>
                                            <input
                                                type="number"
                                                value={appDiscount}
                                                onChange={(e) => setAppDiscount(Math.max(0, Math.min(100, Number(e.target.value))))}
                                                min="0"
                                                max="100"
                                                step="1"
                                            />
                                            <span>%</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* ---------------- FEATURES ---------------- */}
                            <div className="section">
                                <div className="section-header">
                                    <h3>
                                        Feature Add-ons
                                        {selectedFeatureCount > 0 && (
                                            <span className="selection-count">{selectedFeatureCount} selected</span>
                                        )}
                                    </h3>
                                    <Tooltip text={APP_CONFIG.tooltips.features} />
                                </div>
                                <p className="section-note">
                                    Anything your tier already includes costs nothing extra.
                                </p>

                                <div className="selection-grid">
                                    {getOptionalFeatures().map(([key, feature]) => (
                                        <label key={key} className={`selection-item ${selectedFeatures[key] ? 'selected' : ''}`}>
                                            <input
                                                type="checkbox"
                                                checked={!!selectedFeatures[key]}
                                                onChange={() => toggleFeature(key)}
                                            />
                                            <span className="selection-body">
                                                <span className="selection-name">
                                                    <span className="selection-icon">{feature.icon}</span>
                                                    {feature.name}
                                                </span>
                                                <span className="selection-meta">
                                                    <span className="selection-price">
                                                        {formatCurrency(feature.price, true)}/{getPeriodText()}
                                                    </span>
                                                    <span className="badge badge-from">
                                                        Free from {packages[feature.includedFrom].name}
                                                    </span>
                                                </span>
                                            </span>
                                        </label>
                                    ))}
                                </div>

                                <details className="core-features">
                                    <summary>Included in every tier ({getCoreFeatures().length} features)</summary>
                                    <ul>
                                        {getCoreFeatures().map(([key, feature]) => (
                                            <li key={key}>{feature.name}</li>
                                        ))}
                                    </ul>
                                </details>
                            </div>

                            {/* ---------------- USAGE-BASED ADD-ONS ---------------- */}
                            <div className="section">
                                <div className="section-header">
                                    <h3>Usage-based Add-ons</h3>
                                    <Tooltip text={APP_CONFIG.tooltips.usageAddOns} />
                                </div>
                                <p className="section-note">
                                    Billed on what is actually used, so these cost the same on every tier.
                                    Channel bandwidth is invoiced after the month.
                                </p>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>
                                            SSAI &amp; Channel Builder
                                            <Tooltip text={`Server-side ad insertion and channel assembly at ${formatRate(usageCfg.ssaiChannelBuilder.unitPrice)} per channel per month.`} />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">{usageCfg.ssaiChannelBuilder.icon}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={ssaiChannels}
                                                onChange={(e) => setSsaiChannels(Math.max(0, Number(e.target.value)))}
                                            />
                                            <span>channels</span>
                                        </div>
                                        <span className="addon-price">
                                            {formatRate(usageCfg.ssaiChannelBuilder.unitPrice)} per channel / month
                                        </span>
                                    </div>

                                    <div className="input-group">
                                        <label>
                                            Channel bandwidth
                                            <Tooltip text={`Live channel traffic is billed separately from your tier allowance, at ${formatRate(usageCfg.ssaiChannelBuilder.bandwidthPricePerTB)} per TB.`} />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">📶</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={ssaiBandwidthTB}
                                                onChange={(e) => setSsaiBandwidthTB(Math.max(0, Number(e.target.value)))}
                                            />
                                            <span>TB</span>
                                        </div>
                                        <span className="addon-price">
                                            {formatRate(usageCfg.ssaiChannelBuilder.bandwidthPricePerTB)} / TB
                                        </span>
                                    </div>
                                </div>

                                <div className="input-row">
                                    <div className="input-group">
                                        <label>
                                            AI dubbing
                                            <Tooltip text="Automatic dubbing into additional languages, billed per minute of processed audio." />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">{usageCfg.aiDubbing.icon}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={aiDubbingMinutes}
                                                onChange={(e) => setAiDubbingMinutes(Math.max(0, Number(e.target.value)))}
                                            />
                                            <span>min</span>
                                        </div>
                                        <span className="addon-price">
                                            {formatRate(usageCfg.aiDubbing.unitPrice)} / minute
                                        </span>
                                    </div>

                                    <div className="input-group">
                                        <label>
                                            AI subtitles
                                            <Tooltip text="Automatic subtitle generation and translation, billed per minute of processed audio." />
                                        </label>
                                        <div className="input-with-unit">
                                            <span className="input-icon">{usageCfg.aiSubtitles.icon}</span>
                                            <input
                                                type="number"
                                                min="0"
                                                value={aiSubtitleMinutes}
                                                onChange={(e) => setAiSubtitleMinutes(Math.max(0, Number(e.target.value)))}
                                            />
                                            <span>min</span>
                                        </div>
                                        <span className="addon-price">
                                            {formatRate(usageCfg.aiSubtitles.unitPrice)} / minute
                                        </span>
                                    </div>
                                </div>

                                {priced.usage.total > 0 && (
                                    <div className="usage-total">
                                        <span>Usage this month</span>
                                        <strong>{formatCurrency(priced.usage.total, true)}/{getPeriodText()}</strong>
                                    </div>
                                )}
                            </div>

                            {/* ---------------- REVENUE ---------------- */}
                            <div className="section">
                                <h3>Revenue</h3>

                                <div className="input-group">
                                    <label>
                                        Business Model
                                        <Tooltip text="How you generate revenue: SVOD (subscriptions), AVOD (advertising), TVOD (pay-per-view), or HYBRID." />
                                    </label>
                                    <div className="select-with-icon">
                                        <span className="input-icon">💼</span>
                                        <select value={revenueModel} onChange={(e) => setRevenueModel(e.target.value)}>
                                            {Object.entries(APP_CONFIG.revenueModels).map(([key, model]) => (
                                                <option key={key} value={key}>{model.name}</option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {(revenueModel === 'SVOD' || revenueModel === 'HYBRID') && (
                                    <div className="input-row">
                                        <div className="input-group">
                                            <label>
                                                Total Subscribers
                                                <Tooltip text={APP_CONFIG.tooltips.subscribers} />
                                            </label>
                                            <div className="input-with-icon">
                                                <span className="input-icon">👤</span>
                                                <input type="number" value={subscribers} onChange={(e) => setSubscribers(Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label>
                                                Avg Spend
                                                <Tooltip text={APP_CONFIG.tooltips.avgSpend} />
                                            </label>
                                            <div className="input-with-unit">
                                                <span className="input-icon">💰</span>
                                                <span>{getCurrencySymbol(currency)}</span>
                                                <input type="number" value={avgSpend} onChange={(e) => setAvgSpend(Number(e.target.value))} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(revenueModel === 'AVOD' || revenueModel === 'HYBRID') && (
                                    <div className="input-row">
                                        <div className="input-group">
                                            <label>
                                                Total Impressions
                                                <Tooltip text={APP_CONFIG.tooltips.impressions} />
                                            </label>
                                            <div className="input-with-icon">
                                                <span className="input-icon">📊</span>
                                                <input type="number" value={impressions} onChange={(e) => setImpressions(Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label>
                                                Avg CPM
                                                <Tooltip text={APP_CONFIG.tooltips.cpm} />
                                            </label>
                                            <div className="input-with-unit">
                                                <span className="input-icon">📢</span>
                                                <span>{getCurrencySymbol(currency)}</span>
                                                <input type="number" value={cpm} onChange={(e) => setCpm(Number(e.target.value))} />
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {(revenueModel === 'TVOD' || revenueModel === 'HYBRID') && (
                                    <div className="input-row">
                                        <div className="input-group">
                                            <label>
                                                Total Transactions
                                                <Tooltip text={APP_CONFIG.tooltips.transactions} />
                                            </label>
                                            <div className="input-with-icon">
                                                <span className="input-icon">🛒</span>
                                                <input type="number" value={transactions} onChange={(e) => setTransactions(Number(e.target.value))} />
                                            </div>
                                        </div>
                                        <div className="input-group">
                                            <label>
                                                Avg Price
                                                <Tooltip text={APP_CONFIG.tooltips.avgPrice} />
                                            </label>
                                            <div className="input-with-unit">
                                                <span className="input-icon">🏷️</span>
                                                <span>{getCurrencySymbol(currency)}</span>
                                                <input type="number" value={avgPrice} onChange={(e) => setAvgPrice(Number(e.target.value))} />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <div className="action-buttons">
                                <button className="reset-btn" onClick={() => window.location.reload()}>
                                    Reset Calculator
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* ================= COMPARISON ================= */}
                    <div className="right-panel">
                        <div className="panel">
                            <h2>Pricing and Profitability</h2>
                            <p className="panel-subtitle">
                                One platform, four tiers. Apps and features are included by tier — anything else is a monthly add-on.
                            </p>

                            <div className="recommendation-banner">
                                <span className="recommendation-icon">✓</span>
                                <span>
                                    Best fit for these requirements: <strong>{packages[recommendedTier].name}</strong>
                                    {recommendedTier === 'advanced'
                                        ? ' — an estimated subscriber base beyond the published tiers, so this needs a custom quote.'
                                        : <>
                                            {` at ${formatCurrency(tierCosts[recommendedTier].monthlyRecurringCost, true)}/${getPeriodText()} all in`}
                                            {tierCosts[recommendedTier].overageCost > 0
                                                ? `, including ${formatCurrency(tierCosts[recommendedTier].overageCost, true)} of per-GB overage.`
                                                : '.'}
                                            {' It is the cheapest tier whose subscriber guidance covers your estimate of '}
                                            {estimatedSubscribers.toLocaleString()} active subscribers.
                                        </>}
                                </span>
                            </div>

                            <div className="packages-comparison">
                                <div className="package-columns">
                                    <div className="package-header">
                                        <div className="package-label-space"></div>
                                        {TIER_ORDER.map(tierKey => (
                                            <div
                                                key={tierKey}
                                                className={`package-name ${recommendedTier === tierKey ? 'recommended' : ''} ${packages[tierKey].popular ? 'popular' : ''}`}
                                            >
                                                {packages[tierKey].name}
                                                {packages[tierKey].popular && <span className="popular-badge">★ popular</span>}
                                                {recommendedTier === tierKey && <span className="recommended-badge">Best fit</span>}
                                            </div>
                                        ))}
                                    </div>

                                    {/* Plan cost */}
                                    <CostRow
                                        label="Plan Cost"
                                        tooltip="The tier subscription itself, before add-ons and capacity overage."
                                        render={(tierKey, costs) => (
                                            <div className="cost-value plan-price">
                                                {costs.isCustom ? (
                                                    <span className="custom-quote">Contact us</span>
                                                ) : (
                                                    <>
                                                        {formatCurrency(costs.baseCost, true)}/{getPeriodText()}
                                                        {billingType === 'yearly' && (
                                                            <div className="savings-text">Save {calculateYearlySavings(costs.baseCost)}</div>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {/* Bandwidth */}
                                    <CostRow
                                        label="Bandwidth / month"
                                        tooltip={`Included transfer per month. Beyond it, extra bandwidth is billed at ${formatRate(overage.bandwidth.pricePerGB)}/GB.`}
                                        render={(tierKey, costs) => (
                                            <div className={`cost-value ${!costs.fitsBandwidth ? 'insufficient' : ''}`}>
                                                {formatCapacity(packages[tierKey].bandwidth)}
                                                <div className="requirement-info">Required: {requirements.bandwidth.toFixed(2)} TB</div>
                                                {!costs.fitsBandwidth && (
                                                    <div className="overage-note">
                                                        +{Math.round(costs.bandwidthOverGB).toLocaleString()} GB = {formatCurrency(costs.bandwidthOverage, true)}/{getPeriodText()}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {/* Storage */}
                                    <CostRow
                                        label="Storage"
                                        tooltip={`Included storage. Beyond it, extra storage is billed at ${formatRate(overage.storage.pricePerGB)}/GB.`}
                                        render={(tierKey, costs) => (
                                            <div className={`cost-value ${!costs.fitsStorage ? 'insufficient' : ''}`}>
                                                {formatCapacity(packages[tierKey].storage)}
                                                <div className="requirement-info">Required: {requirements.storage.toFixed(2)} TB</div>
                                                {!costs.fitsStorage && (
                                                    <div className="overage-note">
                                                        +{Math.round(costs.storageOverGB).toLocaleString()} GB = {formatCurrency(costs.storageOverage, true)}/{getPeriodText()}
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {/* Active subscribers */}
                                    <CostRow
                                        label="Approx. active subscribers"
                                        tooltip={APP_CONFIG.tooltips.activeSubscribers}
                                        render={(tierKey, costs) => (
                                            <div className={`cost-value ${!costs.fitsSubscribers ? 'insufficient' : ''}`}>
                                                {packages[tierKey].activeSubscribers === Infinity
                                                    ? 'Custom'
                                                    : `~${packages[tierKey].activeSubscribers.toLocaleString()}`}
                                                <div className="requirement-info">Estimated: {estimatedSubscribers.toLocaleString()}</div>
                                                {!costs.fitsSubscribers && (
                                                    <div className="package-warning">Above this tier's guidance</div>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {/* Platform add-ons */}
                                    {!isLegacy && (
                                        <CostRow
                                            label="Platform add-ons"
                                            tooltip="Selected platforms this tier does not already include, at a flat monthly fee each."
                                            render={(tierKey, costs) => (
                                                <div className="cost-value">
                                                    {costs.platformAddOnItems.length === 0 ? (
                                                        <span className="all-included">All included</span>
                                                    ) : (
                                                        <>
                                                            {formatCurrency(costs.platformAddOnCost, true)}/{getPeriodText()}
                                                            <div className="requirement-info">
                                                                {costs.platformAddOnItems.length} × {formatCurrency(150, false)}
                                                            </div>
                                                        </>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    )}

                                    {/* Legacy one-time apps */}
                                    {isLegacy && (
                                        <CostRow
                                            label="One-Time App Costs"
                                            tooltip="Legacy model: app development billed once, up front."
                                            render={(tierKey, costs) => (
                                                <div className="cost-value">
                                                    {formatCurrency(costs.oneTimeAppCosts, false)} one-time
                                                    {costs.legacyUnavailable.length > 0 && (
                                                        <div className="overage-note">
                                                            Not priced under legacy: {costs.legacyUnavailable.join(', ')}
                                                        </div>
                                                    )}
                                                </div>
                                            )}
                                        />
                                    )}

                                    {/* Feature add-ons */}
                                    <CostRow
                                        label="Feature add-ons"
                                        tooltip="Selected features this tier does not already include."
                                        render={(tierKey, costs) => (
                                            <div className="cost-value">
                                                {costs.featureAddOnItems.length === 0 ? (
                                                    <span className="all-included">All included</span>
                                                ) : (
                                                    <>
                                                        {formatCurrency(costs.featureAddOnCost, true)}/{getPeriodText()}
                                                        <div className="requirement-info">
                                                            {costs.featureAddOnItems.map(i => i.name).join(', ')}
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        )}
                                    />

                                    {/* Usage-based add-ons — same on every tier by design */}
                                    <CostRow
                                        label="Usage-based add-ons"
                                        tooltip={APP_CONFIG.tooltips.usageAddOns}
                                        render={(tierKey, costs) => (
                                            <div className="cost-value">
                                                {costs.usageCost > 0 ? (
                                                    <>
                                                        {formatCurrency(costs.usageCost, true)}/{getPeriodText()}
                                                        <div className="requirement-info">
                                                            {costs.usageItems.map(i => i.name).join(', ')}
                                                        </div>
                                                    </>
                                                ) : <span className="all-included">None</span>}
                                            </div>
                                        )}
                                    />

                                    {/* Capacity overage */}
                                    <CostRow
                                        label="Capacity overage"
                                        tooltip={APP_CONFIG.tooltips.overage}
                                        render={(tierKey, costs) => (
                                            <div className={`cost-value ${costs.overageCost > 0 ? 'insufficient' : ''}`}>
                                                {costs.overageCost > 0
                                                    ? `${formatCurrency(costs.overageCost, true)}/${getPeriodText()}`
                                                    : <span className="all-included">None</span>}
                                            </div>
                                        )}
                                    />

                                    {/* Monthly total */}
                                    <CostRow
                                        className="total-row"
                                        label={billingType === 'yearly' ? 'Total Yearly Cost' : 'Total Monthly Cost'}
                                        tooltip="Tier price plus every add-on and any capacity overage."
                                        render={(tierKey, costs) => (
                                            <div className="cost-value total">
                                                {costs.isCustom
                                                    ? <span className="custom-quote">Contact us</span>
                                                    : `${formatCurrency(costs.monthlyRecurringCost, true)}/${getPeriodText()}`}
                                            </div>
                                        )}
                                    />

                                    {/* Revenue */}
                                    <CostRow
                                        label="Total Revenue"
                                        tooltip="Revenue from your selected business model. Identical across tiers."
                                        render={(tierKey) => (
                                            <div className="cost-value">{formatCurrency(revenue, true)}/{getPeriodText()}</div>
                                        )}
                                    />

                                    {/* Net profit */}
                                    <CostRow
                                        className="profit-row"
                                        label={`Net Profit (${billingType === 'yearly' ? 'Yearly' : 'Monthly'})`}
                                        tooltip="Revenue minus total platform cost."
                                        render={(tierKey, costs) => (
                                            <div className={`cost-value profit ${revenue - costs.monthlyRecurringCost < 0 ? 'loss' : ''}`}>
                                                {costs.isCustom
                                                    ? <span className="custom-quote">—</span>
                                                    : `${formatCurrency(revenue - costs.monthlyRecurringCost, true)}/${getPeriodText()}`}
                                            </div>
                                        )}
                                    />
                                </div>
                            </div>

                            <p className="upgrade-note">
                                {upgradeInsight.kind === 'upgrade-wins' && (
                                    <>
                                        <strong>Upgrading wins here:</strong> {packages[upgradeInsight.fullyBundled].name} bundles
                                        all {upgradeInsight.addOnCount} of your add-ons and comes out{' '}
                                        {formatCurrency(upgradeInsight.delta, true)}/{getPeriodText()} cheaper than
                                        stacking them on {packages[upgradeInsight.cheapest].name}.
                                    </>
                                )}
                                {upgradeInsight.kind === 'stack-wins' && (
                                    <>
                                        <strong>Add-ons win here:</strong> keeping {packages[upgradeInsight.cheapest].name} with{' '}
                                        {upgradeInsight.addOnCount} add-on{upgradeInsight.addOnCount === 1 ? '' : 's'} is{' '}
                                        {formatCurrency(upgradeInsight.delta, true)}/{getPeriodText()} cheaper than moving up to{' '}
                                        {packages[upgradeInsight.fullyBundled].name}, which bundles them.
                                        Check the capacity rows before quoting it — a lower tier can still need per-GB overage.
                                    </>
                                )}
                                {upgradeInsight.kind === 'none' && (
                                    <>
                                        Bandwidth and storage beyond a tier are billed per GB
                                        ({formatRate(overage.bandwidth.pricePerGB)}/GB transfer,{' '}
                                        {formatRate(overage.storage.pricePerGB)}/GB storage). Select the platforms
                                        and features you need to compare upgrading against stacking add-ons.
                                    </>
                                )}
                            </p>

                            <ShareQuote
                                quoteState={quoteState}
                                priced={priced}
                                settingsValues={settingsValues}
                            />

                            <PDFExport
                                revenue={revenue}
                                tierCosts={tierCosts}
                                recommendedTier={recommendedTier}
                                requirements={requirements}
                                selectedPlatforms={selectedPlatforms}
                                selectedFeatures={selectedFeatures}
                                pricingMode={pricingMode}
                                revenueModel={revenueModel}
                                currency={currency}
                                formatCurrency={formatCurrency}
                                billingType={billingType}
                                monthlyGrowthRate={monthlyGrowthRate}
                                videoAssets={videoAssets}
                                avgDuration={avgDuration}
                                totalPlays={totalPlays}
                                avgWatchTime={avgWatchTime}
                                videoQuality={videoQuality}
                                estimatedSubscribers={estimatedSubscribers}
                                subscribers={subscribers}
                                avgSpend={avgSpend}
                                impressions={impressions}
                                cpm={cpm}
                                transactions={transactions}
                                avgPrice={avgPrice}
                                appDiscount={appDiscount}
                            />
                        </div>
                    </div>
                </div>

                {showProjections && (
                    <ProjectionsTable
                        revenue={revenue}
                        tierCosts={tierCosts}
                        formatCurrency={formatCurrency}
                        billingType={billingType}
                        monthlyGrowthRate={monthlyGrowthRate}
                        setMonthlyGrowthRate={setMonthlyGrowthRate}
                        pricingMode={pricingMode}
                        currency={currency}
                        revenueModel={revenueModel}
                        revenueDrivers={{ subscribers, impressions, transactions }}
                    />
                )}
            </div>
        </div>
    );
};

export default PricingCalculator;
